/*
 *  ************************************************************************
 *  *  © [2015 - 2025] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { getPath, getTextContrast } from '../../../../../utils';
import { CloseIcon, SuccessCheckIcon } from './icons';
import './styles.m.css';

async function getAccessToken(bridgeKeeperIntegrationId, sketchesHost) {
  const accessTokenUrl = getPath({
    sketchesHost,
    relativePath: `/api/auth/v1/access-token/integrations/${bridgeKeeperIntegrationId}`
  });
  const response = await fetch(accessTokenUrl);
  return response.headers.get('x-integration-token');
}

function useRewardsState(sketchesHost) {
  const opts = useSelector((state) =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'story', 'globalAiElements', 'quiz', 'opts'], {})
  );
  const {
    enabled,
    gameTypeKey,
    gameTypeMode,
    claimButton,
    existingRewardMessage,
    giftSentSuccessfullyMessage,
    recipientPhoneLabel,
    sendAsGiftButton,
    sendGiftButton 
  } = opts || {};

  const bridgeKeeperIntegrationId = useSelector((state) =>
    get(
      state,
      ['qt', 'config', 'pagebuilder-config', 'general', 'accesstypeIntegration', 'bridgeKeeperIntegrationId'],
      ''
    )
  );

  const enableDarkMode = useSelector((state) => get(state, ['header', 'isDarkModeEnabled'], false));
  const general = useSelector((state) => get(state, ['qt', 'config', 'pagebuilder-config', 'general'], {}));
  const { darkPrimaryColor = '#4860BC', primaryColor = '#4860BC' } = general;
  const buttonBgColor = enableDarkMode ? darkPrimaryColor : primaryColor;
  const textColor = getTextContrast(buttonBgColor) === 'dark' ? '#000' : '#fff';

  const gameTypeEnabled = enabled && gameTypeKey;
  const gameTypeHost =
    gameTypeMode === 'staging' ? 'https://gametype.staging.quintype.com' : 'https://gametype.quintype.com';

  const [unclaimedRewards, setUnclaimedRewards] = useState([]);
  const [rewardAlreadyClaimed, setRewardAlreadyClaimed] = useState(false);
  const [actionError, setActionError] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);

  useEffect(() => {
    if (!gameTypeEnabled) return;
    async function fetchProgress() {
      try {
        const accessToken = await getAccessToken(bridgeKeeperIntegrationId, sketchesHost);
        const res = await fetch(`${gameTypeHost}/api/v1/user/progress?publisher_id=${gameTypeKey}`, {
          headers: { Authorization: accessToken }
        });
        if (!res.ok) return;
        const data = await res.json();
        const earnedRewards = get(data, ['earned_rewards']) || [];
        const claimed = earnedRewards.filter((r) => r.status === 'REDEEMED' || r.status === 'GIFTED');
        const unclaimed = earnedRewards.filter((r) => r.status === 'EARNED');

        if (claimed.length > 0) return setRewardAlreadyClaimed(true);
        setUnclaimedRewards(unclaimed);
      } catch (err) {
        console.error('progress fetch error', err);
      }
    }
    fetchProgress();
  }, []);

  const reward = unclaimedRewards[0];

  async function callRewardAction(action, giftTo = '') {
    const accessToken = await getAccessToken(bridgeKeeperIntegrationId, sketchesHost);
    return fetch(`${gameTypeHost}/api/v1/rewards/action?publisher_id=${gameTypeKey}`, {
      method: 'POST',
      headers: { Authorization: accessToken },
      body: JSON.stringify({ reward_id: reward.reward_grant_id, action, gift_to: giftTo })
    });
  }

  async function handleRedeem() {
    try {
      const res = await callRewardAction('REDEEM');

      if (res.status === 409) {
        const data = await res.json();
        if (data.status === 'REDEEMED' || data.status === 'GIFTED') return setRewardAlreadyClaimed(true);
        // user has an active subscription, can't self-redeem — nudge to gift
        return setShowGiftModal(true);
      }

      if (!res.ok) return setActionError(true);

      const data = await res.json();
      if (data.status === 'REDEEMED') setRewardAlreadyClaimed(true);
      else setActionError(true);
    } catch (err) {
      console.error('redeem action error', err);
      setActionError(true);
    }
  }

  async function sendGift(recipient) {
    try {
      const res = await callRewardAction('GIFT', recipient);

      if (res.status === 409) {
        const data = await res.json();
        if (data.status === 'REDEEMED' || data.status === 'GIFTED') {
          setRewardAlreadyClaimed(true);
          return { success: true };
        }
        return { error: data.error || data.message || 'This reward has already been used.' };
      }

      if (!res.ok) return { error: 'Something went wrong. Please try again.' };

      const data = await res.json();
      if (data.status === 'GIFTED') return { success: true };
      return { error: 'Something went wrong. Please try again.' };
    } catch (err) {
      console.error('gift action error', err);
      return { error: 'Something went wrong. Please try again.' };
    }
  }

  return {
    gameTypeEnabled,
    rewardAlreadyClaimed,
    unclaimedRewards,
    actionError,
    showGiftModal,
    setShowGiftModal,
    sendGift,
    handleRedeem,
    markRewardClaimed: () => setRewardAlreadyClaimed(true),
    buttonBgColor,
    textColor,
    enableDarkMode,
    claimButton,
    existingRewardMessage,
    giftSentSuccessfullyMessage,
    recipientPhoneLabel,
    sendAsGiftButton,
    sendGiftButton
  };
}

function GiftModal({
  onClose,
  onGift,
  onGiftSuccess,
  recipientPhoneLabel,
  sendGiftButton,
  giftSentSuccessfullyMessage
}) {
  const [recipient, setRecipient] = useState('');
  const [giftError, setGiftError] = useState(null);
  const [gifted, setGifted] = useState(false);

  async function handleSendGift() {
    const result = await onGift(recipient);
    if (result?.error) setGiftError(result.error);
    else setGifted(true);
  }

  if (gifted) {
    return (
      <div styleName='gift-modal-overlay'>
        <div styleName='gift-modal' data-test-id='quiz-gift-modal'>
          <div styleName='gift-modal-header'>
            <button styleName='gift-modal-close' onClick={onGiftSuccess}>
              <CloseIcon />
            </button>
          </div>
          <SuccessCheckIcon />
          <p styleName='gift-success-message' data-test-id='quiz-gift-sent-success-message'>{giftSentSuccessfullyMessage || 'Gift sent successfully!.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div styleName='gift-modal-overlay'>
      <div styleName='gift-modal' data-test-id='quiz-gift-modal'>
        <button styleName='gift-modal-close' onClick={onClose}>
          <CloseIcon />
        </button>
        <div styleName='gift-modal-wrapper'>
          <div styleName='gift-modal-header'>
            <p styleName='gift-modal-label'>{recipientPhoneLabel ||  "Enter recipient's email or phone number"}</p>
          </div>
          <input
            styleName='gift-modal-input'
            data-test-id='quiz-gift-modal-input'
            type='text'
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
          />
          {giftError && <p styleName='reward-action-error'>{giftError}</p>}
          <button styleName='gift-reward-btn' data-test-id='quiz-send-gift-button' onClick={handleSendGift} disabled={!recipient}>
            {sendGiftButton || 'Send Gift'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionButtons({ onGiftClick, onClaim, userHasSubscriptions, claimButton, sendAsGiftButton }) {
  return (
    <div styleName='action-btns'>
      <button styleName='claim-btn' onClick={onGiftClick}>
        {sendAsGiftButton ||  'Send as Gift'}
      </button>
      <button styleName='claim-btn-secondary' data-test-id='quiz-claim-gift-button' onClick={onClaim} disabled={!!userHasSubscriptions}>
        {claimButton || 'Claim'}
      </button>
    </div>
  );
}

export const Rewards = ({ sketchesHost, userHasSubscriptions }) => {
  const {
    gameTypeEnabled,
    rewardAlreadyClaimed,
    unclaimedRewards,
    actionError,
    showGiftModal,
    setShowGiftModal,
    sendGift,
    handleRedeem,
    markRewardClaimed,
    buttonBgColor,
    textColor,
    enableDarkMode,
    claimButton,
    existingRewardMessage,
    giftSentSuccessfullyMessage,
    recipientPhoneLabel,
    sendAsGiftButton,
    sendGiftButton
  } = useRewardsState(sketchesHost);

  if (!gameTypeEnabled || rewardAlreadyClaimed || unclaimedRewards.length === 0) return null;

  const mode = enableDarkMode ? 'dark' : 'light';
  const buttonStyles = { '--reward-btn-bg': buttonBgColor, '--reward-btn-color': textColor };

  return (
    <div styleName='rewards-wrapper' style={buttonStyles}>
      <div styleName='reward-card' data-test-id='quiz-reward-card' className={mode}>
        <div styleName='reward-header'>{existingRewardMessage || '🎁 You already have an existing reward to claim/gift'}</div>
        {actionError && <div styleName='reward-error'>Something went wrong. Please try again.</div>}
        <ActionButtons
          onGiftClick={() => setShowGiftModal(true)}
          onClaim={handleRedeem}
          userHasSubscriptions={userHasSubscriptions}
          claimButton={claimButton}
          sendAsGiftButton={sendAsGiftButton}
        />
      </div>
      {showGiftModal && (
        <GiftModal
          onClose={() => setShowGiftModal(false)}
          onGift={sendGift}
          onGiftSuccess={() => {
            setShowGiftModal(false);
            markRewardClaimed();
          }}
          recipientPhoneLabel={recipientPhoneLabel}
          sendGiftButton={sendGiftButton}
          giftSentSuccessfullyMessage={giftSentSuccessfullyMessage}
        />
      )}
    </div>
  );
};

